/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { Instant } from './Instant';
import { TimeUnit } from './types';
import { Zone } from './Zone';

const PRECISION = ["hour", "minute", "second", "millisecond", "nanosecond"];
const INSTANT = Symbol("[[Time]]");

export abstract class Clock {
    readonly zone: Zone;

    constructor(zone: Zone) {
        this.zone = zone;
    }

    static get default() { return Clock.getSystem(Zone.SYSTEM); }
    static get UTC() { return Clock.getSystem(Zone.UTC); }

    abstract currentInstant(): Instant;

    abstract withZone(zone: Zone | string): Clock;

    withPrecision(precision: TimeUnit): Clock {
        const newPrecision = PRECISION.indexOf(precision);
        if (newPrecision === -1) throw new RangeError();

        if (this instanceof TruncatingClock) {
            const oldPrecision = PRECISION.indexOf(this._precision);
            if (oldPrecision <= newPrecision) return this;

            return new TruncatingClock(this._clock, precision);
        }

        return new TruncatingClock(this, precision);
    }

    static getSystem(zone: Zone): Clock {
        return new SystemClock(zone);
    }

    static getFixed(zone: Zone, instant: Instant): Clock {
        return new FixedClock(zone, instant);
    }
}

Object.defineProperty(Clock.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "Clock"
});

class SystemClock extends Clock {
    constructor(zone: Zone) {
        super(zone);
    }

    currentInstant() {
        return Instant.fromMilliseconds(Date.now());
    }

    withZone(zone: Zone | string) {
        if (typeof zone === "string") zone = Zone.fromString(zone);
        return new SystemClock(zone);
    }
}

class FixedClock extends Clock {
    private [INSTANT]: Instant;

    constructor(zone: Zone, instant: Instant) {
        super(zone);
        this[INSTANT] = instant;
    }

    currentInstant() {
        return this[INSTANT];
    }

    withZone(zone: Zone | string) {
        if (typeof zone === "string") zone = Zone.fromString(zone);
        return new FixedClock(zone, this[INSTANT]);
    }
}

class TruncatingClock extends Clock {
    readonly _precision: TimeUnit;
    readonly _clock: Clock;

    constructor(clock: Clock, precision: TimeUnit) {
        super(clock.zone);
        this._clock = clock;
        this._precision = precision;
    }

    currentInstant() {
        return this._clock.currentInstant().truncate(this._precision);
    }

    withPrecision(precision: TimeUnit): Clock {
        const newPrecision = PRECISION.indexOf(precision);
        if (newPrecision === -1) throw new RangeError();

        const oldPrecision = PRECISION.indexOf(this._precision);
        if (oldPrecision <= newPrecision) return this;

        return new TruncatingClock(this._clock, precision);
    }

    withZone(zone: Zone | string) {
        return new TruncatingClock(this._clock.withZone(zone), this._precision);
    }
}
